import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { resendOtp, verifyEmailOtp, type User } from '../../../api/authApi';
import { useAuth } from '../../../context/AuthContext';
import { useBilling } from '../../../context/BillingContext';
import { getDashboardPathForUser } from '../../../utils/dashboardRouting';
import {
  Eyebrow,
  PageHeading,
  InlineAlert,
  TrustStrip,
} from '../../../design-system';
import styles from './EmailVerificationPage.module.css';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;

type MessageState = { type: 'error' | 'success'; text: string } | null;

// Note: alert icons now come from <InlineAlert> in the design system.
// Old ErrorIcon / SuccessIcon definitions were removed since they're unused.

export const EmailVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithResponse } = useAuth();
  const { ensureTrial, subscription } = useBilling();

  const verificationState = (location.state as { email?: string; redirectTo?: string } | null) ?? null;
  const emailFromState: string = verificationState?.email ?? '';
  const redirectTo = verificationState?.redirectTo ?? '';
  const emailFromQuery = new URLSearchParams(location.search).get('email') ?? '';
  const email = emailFromState || emailFromQuery;

  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<MessageState>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const autoDismissRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Auto-dismiss success messages (not the verified redirect one)
  useEffect(() => {
    if (autoDismissRef.current) clearTimeout(autoDismissRef.current);
    if (message?.type === 'success' && !message.text.includes('Email verified')) {
      autoDismissRef.current = setTimeout(() => setMessage(null), 5000);
    }
    return () => {
      if (autoDismissRef.current) clearTimeout(autoDismissRef.current);
    };
  }, [message]);

  const showError = (text: string) => setMessage({ type: 'error', text });
  const showSuccess = (text: string) => setMessage({ type: 'success', text });
  const dismissMessage = () => setMessage(null);

  const resolveNextPath = useCallback((user: User | undefined): string => {
    const pendingPlan = sessionStorage.getItem('pending_plan_tier');
    if (pendingPlan) {
      sessionStorage.removeItem('pending_plan_tier');
      return `/billing/checkout?plan=${pendingPlan}`;
    }

    if (redirectTo && !['/login', '/signup', '/verify-email'].includes(redirectTo)) {
      return redirectTo;
    }

    if (user?.is_enterprise_member && !user.onboarding_completed) {
      return '/enterprise/onboarding';
    }

    const dashboardPath = getDashboardPathForUser(
      subscription?.plan_tier,
      user?.organization_type,
      user?.is_enterprise_member,
    );
    if (dashboardPath) {
      return dashboardPath;
    }

    if (!user?.onboarding_completed) {
      return '/billing';
    }

    return '/dashboard';
  }, [redirectTo, subscription?.plan_tier]);

  const handleDigitChange = useCallback(
    (index: number, value: string) => {
      // Clear any existing message when the user starts typing
      setMessage((prev) => (prev?.type === 'error' ? null : prev));

      const cleaned = value.replace(/\D/g, '');
      if (cleaned.length > 1) {
        const newDigits = [...digits];
        for (let i = 0; i < cleaned.length && index + i < OTP_LENGTH; i++) {
          newDigits[index + i] = cleaned[i];
        }
        setDigits(newDigits);
        const nextFocus = Math.min(index + cleaned.length, OTP_LENGTH - 1);
        inputRefs.current[nextFocus]?.focus();
        return;
      }
      const newDigits = [...digits];
      newDigits[index] = cleaned;
      setDigits(newDigits);
      if (cleaned && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [digits],
  );

  const handleKeyDown = useCallback(
    (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Backspace' && !digits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [digits],
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const otp = digits.join('');
    if (otp.length < OTP_LENGTH) {
      showError('Please enter all 6 digits of your verification code.');
      return;
    }
    if (!email) {
      showError('Could not determine your email address. Please go back and sign up again.');
      return;
    }

    setMessage(null);
    setSubmitting(true);
    try {
      const result = await verifyEmailOtp({ email, otp });
      if (result.verified) {
        loginWithResponse(result);
        try {
          await ensureTrial();
        } catch {
          // Do not block the redirect if trial setup is unavailable
        }

        const nextPath = resolveNextPath(result.user);
        showSuccess('Email verified successfully! Redirecting you now…');
        setTimeout(() => navigate(nextPath, { replace: true }), 1200);
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Verification failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!email || resendCooldown > 0 || resending) return;
    setResending(true);
    setMessage(null);
    try {
      await resendOtp(email);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      showSuccess('A new verification code has been sent to your email inbox.');
      setDigits(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.themeToggle}>
      </div>

      <div className={styles.card}>
        <img
          src="/Policy Ora Final Logo.png"
          alt="PolicyOra"
          className={styles.logoImage}
          draggable={false}
        />

        <Eyebrow tone="teal" className={styles.eyebrow}>
          VERIFY YOUR EMAIL
        </Eyebrow>

        <PageHeading
          accentWord="email"
          accentTone="blue"
          align="center"
          size="h2"
          className={styles.headingOverride}
        >
          Confirm your email to continue
        </PageHeading>

        <p className={styles.subtext}>
          We sent a 6-digit verification code to{' '}
          {email ? (
            <span className={styles.emailHighlight}>{email}</span>
          ) : (
            'your email address'
          )}
          . Enter it below to activate your account. The code expires in 10 minutes.
          If you do not see the email in your inbox, please check your spam or junk folder.
        </p>

        {message && (
          <InlineAlert
            variant={message.type === 'error' ? 'danger' : 'success'}
            title={message.text}
            onDismiss={dismissMessage}
            className={styles.alertOverride}
          />
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className={styles.otpInputRow} aria-label="Verification code">
            {digits.map((digit, i) => (
              <input
                key={i}
                ref={(el) => { inputRefs.current[i] = el; }}
                className={`${styles.otpDigit} ${
                  message?.type === 'error' ? styles.otpDigitError : ''
                }`}
                type="text"
                inputMode="numeric"
                maxLength={OTP_LENGTH}
                value={digit}
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                aria-label={`Digit ${i + 1}`}
                onChange={(e) => handleDigitChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
              />
            ))}
          </div>

          <button
            type="submit"
            className={styles.submitButton}
            disabled={submitting || digits.join('').length < OTP_LENGTH}
          >
            {submitting ? (
              <span className={styles.submitSpinner}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={styles.spinIcon} aria-hidden>
                  <path d="M12 2a10 10 0 0 1 10 10" />
                </svg>
                Verifying…
              </span>
            ) : 'Verify email'}
          </button>
        </form>

        <div className={styles.resendRow}>
          Didn&apos;t receive a code? Check your spam folder or{' '}
          <button
            type="button"
            className={styles.resendButton}
            disabled={resendCooldown > 0 || resending}
            onClick={handleResend}
          >
            {resending ? 'Sending…' : 'Resend code'}
          </button>
          {resendCooldown > 0 && (
            <span className={styles.countdown}> — resend in {resendCooldown}s</span>
          )}
        </div>

        <TrustStrip className={styles.trustOverride} />
      </div>
    </div>
  );
};
